/**
 * Cohort Management API Service
 * Handles all API calls related to cohorts and participants
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nh9pshlcw7.execute-api.us-west-2.amazonaws.com/prod';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Get access token from MSAL
 */
const getAccessToken = async () => {
  // This will be injected by the component
  return null;
};

/**
 * Set access token getter function
 */
export const setTokenGetter = (getter) => {
  apiClient.interceptors.request.use(async (config) => {
    const token = await getter();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
};

// ============================================================================
// COHORT ENDPOINTS
// ============================================================================

/**
 * Get all cohorts with pagination support
 */
export const getCohorts = async (filters = {}) => {
  const params = new URLSearchParams();
  
  // Status filter (supports comma-separated values)
  if (filters.status) params.append('status', filters.status);
  
  // Program level filter
  if (filters.program_level) params.append('program_level', filters.program_level);
  
  // Pagination parameters
  if (filters.page) params.append('page', filters.page);
  if (filters.limit) params.append('limit', filters.limit);
  
  const response = await apiClient.get(`/leading/api/cohorts?${params}`);
  return response.data;
};

/**
 * Get cohort by ID
 */
export const getCohort = async (cohortId) => {
  const response = await apiClient.get(`/leading/api/cohorts/${cohortId}`);
  return response.data;
};

/**
 * Create new cohort
 */
export const createCohort = async (cohortData) => {
  const response = await apiClient.post('/leading/api/cohorts', cohortData);
  return response.data;
};

/**
 * Update cohort
 */
export const updateCohort = async (cohortId, cohortData) => {
  const response = await apiClient.put(`/leading/api/cohorts/${cohortId}`, cohortData);
  return response.data;
};

/**
 * Delete cohort
 */
export const deleteCohort = async (cohortId) => {
  const response = await apiClient.delete(`/leading/api/cohorts/${cohortId}`);
  return response.data;
};

// ============================================================================
// PARTICIPANT ENDPOINTS
// ============================================================================

/**
 * Get participants in a cohort
 */
export const getParticipants = async (cohortId, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  
  const response = await apiClient.get(`/leading/api/cohorts/${cohortId}/participants?${params}`);
  return response.data;
};

/**
 * Enroll participant in cohort
 */
export const enrollParticipant = async (cohortId, participantData) => {
  const response = await apiClient.post(`/leading/api/cohorts/${cohortId}/participants`, participantData);
  return response.data;
};

/**
 * Update participant
 */
export const updateParticipant = async (cohortId, participantId, participantData) => {
  const response = await apiClient.put(`/leading/api/cohorts/${cohortId}/participants/${participantId}`, participantData);
  return response.data;
};

/**
 * Withdraw participant
 */
export const withdrawParticipant = async (cohortId, participantId) => {
  const response = await apiClient.delete(`/leading/api/cohorts/${cohortId}/participants/${participantId}`);
  return response.data;
};

// ============================================================================
// ADHOC RECIPIENT ENDPOINTS
// ============================================================================

/**
 * Get adhoc recipients for a participant and survey
 */
export const getAdhocRecipients = async (participantId, surveyId) => {
  const response = await apiClient.get(`/leading/api/participants/${participantId}/adhoc-recipients?survey_id=${surveyId}`);
  return response.data;
};

/**
 * Add adhoc recipient (survey-specific)
 */
export const addAdhocRecipient = async (participantId, recipientData) => {
  // recipientData should include survey_id
  const response = await apiClient.post(`/leading/api/participants/${participantId}/adhoc-recipients`, recipientData);
  return response.data;
};

/**
 * Remove adhoc recipient
 */
export const removeAdhocRecipient = async (participantId, recipientId) => {
  const response = await apiClient.delete(`/leading/api/participants/${participantId}/adhoc-recipients/${recipientId}`);
  return response.data;
};

// ============================================================================
// USER SEARCH ENDPOINTS
// ============================================================================

/**
 * Search EntraID users
 */
export const searchUsers = async (query, limit = 10) => {
  const response = await apiClient.get(`/leading/api/users/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  return response.data;
};

/**
 * Get user details
 */
export const getUserDetails = async (employeeId) => {
  const response = await apiClient.get(`/leading/api/users/${employeeId}`);
  return response.data;
};

/**
 * Get user's direct reports
 */
export const getUserDirectReports = async (employeeId) => {
  const response = await apiClient.get(`/leading/api/users/${employeeId}/direct-reports`);
  return response.data;
};

/**
 * Get user's manager
 */
export const getUserManager = async (employeeId) => {
  const response = await apiClient.get(`/leading/api/users/${employeeId}/manager`);
  return response.data;
};

export default {
  getCohorts,
  getCohort,
  createCohort,
  updateCohort,
  deleteCohort,
  getParticipants,
  enrollParticipant,
  updateParticipant,
  withdrawParticipant,
  getAdhocRecipients,
  addAdhocRecipient,
  removeAdhocRecipient,
  searchUsers,
  getUserDetails,
  getUserDirectReports,
  getUserManager,
  setTokenGetter
};
